import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Offer } from '@/services/PifService'
import { GUEST_STRIPE_PAYMENT_INTENT, STRIPE_PAYMENT_INTENT } from '@/services/StripeService/queries'
import { useUser } from '@/services/UserService'
import {
  GuestStripePaymentIntentMutation,
  GuestStripePaymentIntentMutationVariables,
  StripePaymentIntentMutation,
  StripePaymentIntentMutationVariables,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'

interface Props {
  offer: Offer | undefined | null
}

export const useClientSecret = ({ offer }: Props) => {
  const { user, loading: isUserLoading } = useUser()
  const [clientSecret, setClientSecret] = useState<string | null>(null)

  const [generatePaymentIntent, { loading }] = useMutation<
    StripePaymentIntentMutation,
    StripePaymentIntentMutationVariables
  >(STRIPE_PAYMENT_INTENT, {
    errorPolicy: 'all',
  })

  const [generateGuestPaymentIntent, { loading: guestLoading }] = useMutation<
    GuestStripePaymentIntentMutation,
    GuestStripePaymentIntentMutationVariables
  >(GUEST_STRIPE_PAYMENT_INTENT, {
    errorPolicy: 'all',
  })

  const createClientSecret = useCallback(
    async (offer: Offer, isGuest: boolean) => {
      try {
        if (isGuest) {
          const { data } = await generateGuestPaymentIntent({
            variables: {
              input: {
                amount: offer.price,
                offerSlug: offer.slug,
              },
            },
          })

          return data?.guestStripePaymentIntentMutation?.clientSecret ?? null
        }

        const { data } = await generatePaymentIntent({
          variables: {
            input: {
              amount: offer.price,
              offerSlug: offer.slug,
            },
          },
        })

        return data?.stripePaymentIntentMutation?.clientSecret ?? null
      } catch (error) {
        logger().error(
          'Failed to generate client secret from stripe payment intent mutation',
          { offer, isGuest },
          error,
        )
        throw error
      }
    },
    [generateGuestPaymentIntent, generatePaymentIntent],
  )

  useEffect(() => {
    if (isUserLoading) return
    if (!offer) return

    setClientSecret(null)

    createClientSecret(offer, !user).then((secret) => setClientSecret(secret))
  }, [createClientSecret, isUserLoading, offer, user])

  return useMemo(() => {
    if (isUserLoading) return { clientSecret: null, loading: true }

    return { clientSecret, loading: loading || guestLoading }
  }, [clientSecret, guestLoading, isUserLoading, loading])
}
